import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { extras } from "../i18n";
import { API, CONTACT, WHATSAPP_NUMBER } from "../config";
import { MapPin, Mail, Clock, Navigation, Phone } from "lucide-react";

export default function Contact() {
  const { t, lang } = useLang();
  const x = extras[lang] || extras.es;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error(x.privacyRequired);
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, { ...form, accepted_privacy: privacy });
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", message: "" });
      setPrivacy(false);
    } catch (err) {
      if (err.response?.data?.detail === "privacy_required") toast.error(x.privacyRequired);
      else toast.error(t.booking.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="border-t-2 border-topacio-ink bg-white">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <p className="eyebrow mb-4">{t.contact.eyebrow}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-topacio-ink">{t.contact.title}</h2>
            <p className="mt-4 text-topacio-faded max-w-md">{t.contact.sub}</p>
            <div className="mt-10 space-y-5 text-sm font-semibold">
              <div className="flex items-center gap-4 text-topacio-ink">
                <span className="w-10 h-10 shrink-0 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#111]">
                  <MapPin className="w-5 h-5" />
                </span>
                {t.contact.address}
              </div>
              <div className="flex items-center gap-4 text-topacio-ink">
                <span className="w-10 h-10 shrink-0 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#111]">
                  <Mail className="w-5 h-5" />
                </span>
                <span>
                  <a href={`mailto:${CONTACT.email}`} className="hover:text-topacio-red transition-colors block">{CONTACT.email}</a>
                </span>
              </div>
              <div className="flex items-center gap-4 text-topacio-ink">
                <span className="w-10 h-10 shrink-0 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#111]">
                  <Phone className="w-5 h-5" />
                </span>
                <span>
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:text-topacio-red transition-colors block">{CONTACT.phone}</a>
                  <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola! Quiero alquilar una moto en Tenerife.")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="text-topacio-faded hover:text-topacio-red transition-colors block">WhatsApp {CONTACT.whatsappDisplay}</a>
                </span>
              </div>
              <div className="flex items-center gap-4 text-topacio-ink">
                <span className="w-10 h-10 shrink-0 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#111]">
                  <Clock className="w-5 h-5" />
                </span>
                {t.contact.hours}
              </div>
            </div>
          </div>
          <form onSubmit={submit} data-testid="contact-form" className="card-brutal p-8 space-y-5 h-fit">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input data-testid="contact-name-input" required placeholder={t.contact.name} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-brutal" />
              <input data-testid="contact-email-input" required type="email" placeholder={t.contact.email} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-brutal" />
            </div>
            <input data-testid="contact-phone-input" placeholder={t.contact.phone} value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-brutal" />
            <textarea data-testid="contact-message-input" required rows={4} placeholder={t.contact.message} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-brutal" />
            <label data-testid="contact-privacy-label" className="flex items-start gap-3 cursor-pointer select-none bg-topacio-sand border-2 border-dashed border-topacio-ink/30 rounded-lg p-3.5">
              <input type="checkbox" data-testid="contact-privacy-checkbox" checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-0.5 w-4 h-4 shrink-0 accent-[#E63946] cursor-pointer" />
              <span className="text-[11px] leading-snug text-topacio-faded font-semibold">
                {x.privacyPre}{" "}
                <Link to="/privacidad" target="_blank" data-testid="contact-privacy-link" className="font-extrabold text-topacio-red underline underline-offset-2 hover:text-topacio-ink">
                  {x.privacyLink}
                </Link>
                {x.privacyPost ? ` ${x.privacyPost}` : ""}
              </span>
            </label>
            <button data-testid="contact-send-btn" disabled={busy || !privacy} className="btn-primary">
              {t.contact.send}
            </button>
          </form>
        </div>

        <div data-testid="office-map" className="card-brutal overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b-2 border-topacio-ink bg-brand">
            <p className="font-display font-bold text-topacio-ink flex items-center gap-2">
              <MapPin className="w-5 h-5" /> {t.contact.findUs}
            </p>
            <a href={CONTACT.directions} target="_blank" rel="noopener noreferrer" data-testid="directions-btn"
              className="btn-secondary !px-5 !py-2 !text-xs">
              <Navigation className="w-4 h-4" /> {t.contact.directions}
            </a>
          </div>
          <iframe title="Mapa oficina Rent a Moto Topacio" src={CONTACT.mapEmbed}
            className="w-full h-[380px] border-0" loading="lazy" />
        </div>
      </div>
    </section>
  );
}
